import { tint } from 'polished';
import React from 'react';
import styled, { css } from 'styled-components';
import { pxInRem } from '../../../CodesThatsGottaGoSomewhereElse';
import { ColorGroup, Colors, toRem } from '../../../ui';
import { ElementSelector } from '../../canvas/ui/ElementSelector';
import { Flexer } from '../../dashboard/elements/LayoutElements';
import { AutoLayoutMode } from '../autoLayout/AutoLayout';

const ZoneSkele = styled.div<{ width: any, color?: string }>`
    display: inline-block;
    width: ${p => toRem(p.width)};
    height: .375rem;
    margin-right: .25rem;
    background: ${p => p.color ?? Colors.stone300};
    border-radius: .125rem;
    vertical-align: top;
`;

const FrameFrame = styled.div`
    display: flex;
    flex-direction: column;
    position: relative;
    flex: 1 1 auto;
    min-height: 8rem;
    min-width: 9rem;
    color: ${Colors.stone400};
`;

const FrameLabel = styled.div`
    margin-left: .25rem
    flex: 0 0 auto;
    font-size: .75rem;
`;

const FrameBorder = styled.div<{ border: [number, number, number, number], color: string, dashed?: boolean }>`
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    position: relative;
    gap: .125rem;
    padding: .125rem;
    border-radius: .25rem;
    background: #fff;
    border: solid .125rem ${Colors.stone300};
    ${p => p.border[0] > 0 ? css`border-left: solid ${tint(.5, p.color)} ${pxInRem(p.border[0])}rem}` : ''};
    ${p => p.border[1] > 0 ? css`border-top: solid ${tint(.5, p.color)} ${pxInRem(p.border[1])}rem}` : ''};
    ${p => p.border[2] > 0 ? css`border-right: solid ${tint(.5, p.color)} ${pxInRem(p.border[2])}rem}` : ''};
    ${p => p.border[3] > 0 ? css`border-bottom: solid ${tint(.5, p.color)} ${pxInRem(p.border[3])}rem}` : ''};
`;

const FrameColumn = styled.div`
    flex: 1 1 auto;
    border-right: solid 1px ${Colors.stone300};

    &:last-child {
        border: none;
    }
`;

const FrameRow = styled.div`
    flex: 1 1 auto;
    border-bottom: solid 1px ${Colors.stone300};

    &:last-child {
        border: none;
    }
`;

const Activity = React.memo<any>(({ border, color }) => {
    border = border.map(x => x * .33);
    return <FrameFrame>
        <FrameLabel>Activity Frame</FrameLabel>
        <FrameBorder {...{ border, color }} />
    </FrameFrame>;
});

const Grid = React.memo<any>(({ label, border, color, dimensions }) => {
    const [cols, rows] = dimensions ?? [5, 5];
    border = border.map(x => x * .33);
    return <FrameFrame>
        <FrameLabel>{label}</FrameLabel>
        <FrameBorder {...{ border, color }}>
            <Flexer inset="0" flex="row" align="stretch" justify="space-between">
                {Array.from({ length: cols }).map((_2, c) =>
                    <FrameColumn key={`x${c}`} />
                )}
            </Flexer>
            <Flexer inset="0" flex="column" align="stretch" justify="space-between">
                {Array.from({ length: rows }).map((_2, r) =>
                    <FrameRow key={`x${r}`} />
                )}
            </Flexer>
        </FrameBorder>
    </FrameFrame>;
});

const MonthRow = styled.div`
    flex: 1 1 auto;
    display: flex;
    flex-direction: row;
    align-items: stretch;
    gap: .125rem;
`;

const MonthDay = styled.div`
    flex: 1 1 auto;
    background: ${Colors.stone50};
`;

const FrameHeader = styled.div<{ height: any, color: string }>`
    height: ${p => toRem(p.height)};
    background: ${p => p.color};
`;

const Timeline = React.memo<any>(({ border, color, special }) => {
    border = border.map(x => x * .33);
    return <FrameFrame>
        <FrameLabel>Timeline Frame</FrameLabel>
        <FrameBorder {...{ border, color }}>
            <FrameHeader height="1" color={ColorGroup.findPeer(color, 3)} />
            <Flexer grow flex="row" align="stretch" justify="space-between">
                {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'].map((x, i) =>
                    <FrameColumn key={x} />
                )}
            </Flexer>
        </FrameBorder>
    </FrameFrame>;
});

const Month = React.memo<any>(({ border, color, special }) => {
    border = border.map(x => x * .33);
    return <FrameFrame>
        <FrameLabel>Month Frame</FrameLabel>
        <FrameBorder {...{ border, color }}>
            <FrameHeader height=".5" color={ColorGroup.findPeer(color, 2)} />
            {Array.from({ length: 5 }).map((_1, r) =>
                <MonthRow key={r}>
                    {Array.from({ length: 7 }).map((_2, d) => <MonthDay key={`${r}:${d}`} />)}
                </MonthRow>
            )}
        </FrameBorder>
    </FrameFrame>;
});

const defaultsZoneProps = {
    color: '',
    border: [0, 0, 0, 0],
    headingPosition: 'left',
    headingOffset: 0,
    titleColor: '',
    headingStyle: 'classic',
};

const ZoneItem = (label: string, props: any, preview: React.ComponentType<any>) =>
    ElementSelector.Item(`frames/${label}`, label, { ...defaultsZoneProps, ...props, label }, ElementSelector.ItemPreview(preview));

const gridConfig = (dims: number) => ({ addon: 'grid', dimensions: [dims, dims], layoutMode: ['flex', 'row', 'start', 'start'] });
const colsConfig = (cols: number) => ({ addon: 'grid', dimensions: [cols, 1], layoutMode: ['flex', 'column', 'center', 'start'] });
const rowsConfig = (rows: number) => ({ addon: 'grid', dimensions: [1, rows], layoutMode: ['flex', 'row', 'center', 'start'] });

export const FrameElementSelectorItems = (color: string) => [
    ElementSelector.ItemGroup('frames', 'Frames', 'deqo://icons/d2/frame', [
        ElementSelector.ItemSet('frames', 'Frames', 2, [
            ZoneItem('Activity Frame', { color, addon: 'activity' }, Activity),
            ZoneItem('Timeline Frame', { color, addon: 'timeline', width: 3000, height: 1000, layoutMode: AutoLayoutMode.none }, Timeline),
            // ZoneItem('Calendar Frame', { color, addon: 'timeline', width: 3000, height: 1000 }, Timeline),
            // ZoneItem('Month Planner', { color, addon: 'calendar', width: 1800, height: 1200, background: '#F6FAFD' }, Month),
            // ZoneItem('Grid Frame', { color, addon: 'grid', dimensions: [5, 5] }, Grid),
            // ]),
            // ElementSelector.ItemSet('gridFrames', 'Grid Frames', 2, [
            ZoneItem('2x2 Grid Frame', { color, ...gridConfig(2) }, Grid),
            ZoneItem('3x3 Grid Frame', { color, ...gridConfig(3) }, Grid),
            ZoneItem('4x4 Grid Frame', { color, ...gridConfig(4) }, Grid),
            ZoneItem('5x5 Grid Frame', { color, ...gridConfig(5) }, Grid),
            ZoneItem('2 Column Frame', { color, ...colsConfig(2) }, Grid),
            ZoneItem('3 Column Frame', { color, ...colsConfig(3) }, Grid),
            ZoneItem('4 Column Frame', { color, ...colsConfig(4) }, Grid),
            ZoneItem('5 Column Frame', { color, ...colsConfig(5) }, Grid),
            ZoneItem('2 Row Frame', { color, ...rowsConfig(2) }, Grid),
            ZoneItem('3 Row Frame', { color, ...rowsConfig(3) }, Grid),
            ZoneItem('4 Row Frame', { color, ...rowsConfig(4) }, Grid),
            ZoneItem('5 Row Frame', { color, ...rowsConfig(5) }, Grid),
        ]),
        // ElementSelector.ItemSet('experimentalFrames', 'Experimental Frames', 2, [
        // ]),
    ]),
];


